/**
 * Date Cell Renderer
 *
 * Renders formatted date display for vulnerability timestamps.
 * Used for both "First Seen" (created) and "Last Seen" (visited) columns.
 */

import React from 'react';

import type { RiskWithVulnerability } from '@/hooks/useRisks';

interface DateCellProps {
  risk: RiskWithVulnerability;
  /** The date value to display */
  value?: string | number | Date;
  /** Field name for accessing date from risk object */
  field?: 'created' | 'visited';
}

/**
 * Memoized cell renderer for date columns
 *
 * This is a placeholder component as the table component handles date formatting
 * automatically when cell type is set to 'date'. This component is provided for
 * explicit type safety and future customization needs.
 *
 * The actual date formatting is handled by the Table component's built-in
 * 'date' cell renderer when column.cell = 'date' is specified.
 *
 * @param risk - The vulnerability/risk
 * @param value - The date value (provided by table component)
 * @param field - Optional field name to access from risk object
 * @returns Formatted date (handled by table component) or dash if unavailable
 */
export const DateCell: React.FC<DateCellProps> = React.memo(
  ({ risk, value, field }) => {
    const dateValue = value ?? (field ? risk[field] : null);

    if (dateValue === undefined || dateValue === null) {
      return <>-</>;
    }

    // The table component handles date formatting when cell type is 'date'
    // This component is mainly for type safety and explicit component usage
    return <>{dateValue}</>;
  }
);

DateCell.displayName = 'DateCell';
